import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { Spin, Empty } from "antd";
import { LockOutlined, PictureOutlined, FileImageOutlined } from "@ant-design/icons";

import { Post, getPostsBySubcategory, getPostData, getImageList, getGrossImageList, isPostDetailBlocked } from "../api/postAPI";
import { MainColor, SubColor } from "../style/styleValue";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 10;
const photoUrl = (path: string) => `${BACKEND}/api/photo/${path}`;
const grossUrl = (path: string) => `${BACKEND}/api/gross-photo/file/${path}`;

type MediaKind = "slide" | "image" | "gross";

interface MediaThumb {
  key: string;
  url: string;
  kind: MediaKind;
  label?: string;
}

interface PostMedia {
  thumbs: MediaThumb[];
  slideCount: number;
  imageCount: number;
  loading: boolean;
  locked: boolean;
}

const emptyMedia: PostMedia = { thumbs: [], slideCount: 0, imageCount: 0, loading: true, locked: false };

const Gallery = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();

  const [posts, setPosts] = useState<Post[]>([]);
  const [mediaByPost, setMediaByPost] = useState<Record<number, PostMedia>>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // 다음에 불러올 페이지 번호. 진행 중 요청과 언마운트 여부를 ref로 추적한다.
  const pageRef = useRef(1);
  const fetchingRef = useRef(false);
  const cancelledRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const organName = searchParams.get("name") || "갤러리";

  // 게시글 한 페이지(10개)를 이어서 불러오고, 각 게시글의 미디어를 병렬로 채운다.
  const loadNextPage = useCallback(async () => {
    if (!categoryId || fetchingRef.current || !hasMore) return;
    fetchingRef.current = true;

    const page = pageRef.current;
    if (page === 1) setInitialLoading(true);
    else setLoadingMore(true);

    try {
      const res: Post[] = await getPostsBySubcategory(
        page,
        PAGE_SIZE,
        +categoryId,
        "modifiedAt",
        "DESC",
        null,
        null
      );
      if (cancelledRef.current) return;

      const batch = Array.isArray(res) ? res : [];
      setPosts((prev) => {
        // 혹시 모를 중복(id) 방지
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...batch.filter((p) => !seen.has(p.id))];
      });
      setMediaByPost((prev) => ({
        ...prev,
        ...Object.fromEntries(batch.map((p) => [p.id, { ...emptyMedia }])),
      }));

      // 받은 개수가 PAGE_SIZE 미만이면 마지막 페이지
      if (batch.length < PAGE_SIZE) setHasMore(false);
      pageRef.current = page + 1;

      // 이번 페이지 게시글들의 미디어를 병렬로 채운다.
      batch.forEach(async (post) => {
        const media = await fetchPostMedia(post);
        if (!cancelledRef.current) {
          setMediaByPost((prev) => ({ ...prev, [post.id]: media }));
        }
      });
    } catch (e) {
      console.error("Gallery load error:", e);
      if (!cancelledRef.current) setHasMore(false);
    } finally {
      if (!cancelledRef.current) {
        setInitialLoading(false);
        setLoadingMore(false);
      }
      fetchingRef.current = false;
    }
  }, [categoryId, hasMore]);

  // 카테고리 변경 시 초기화 후 첫 페이지 로드
  useEffect(() => {
    cancelledRef.current = false;
    pageRef.current = 1;
    setPosts([]);
    setMediaByPost({});
    setHasMore(true);
    setInitialLoading(true);
    loadNextPage();

    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // 스크롤이 하단 센티널에 닿으면 다음 페이지 로드.
  // 센티널은 첫 페이지 로드 후(initialLoading=false, posts>0)에야 렌더되므로,
  // 그 시점과 목록이 늘어날 때마다 옵저버를 재부착해 교차 상태를 다시 평가한다.
  // (콘텐츠가 화면보다 짧으면 재생성 시 즉시 다음 페이지를 이어 불러온다.)
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadNextPage();
      },
      { rootMargin: "300px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadNextPage, initialLoading, posts.length, hasMore]);

  const goToPost = (post: Post) => {
    const params = new URLSearchParams();
    params.set("title", post.title);
    params.set("from", "gallery");
    if (organName !== "갤러리") params.set("name", organName);
    if (categoryId) params.set("categoryId", categoryId);
    navigate(`/post/${post.id}?${params.toString()}`);
  };

  // 미디어(슬라이드/이미지/그로스)가 하나라도 있는 게시글만 노출한다.
  // 미디어는 게시글별로 비동기 로드되므로, 로딩이 끝났고 썸네일이 1개 이상인 것만 렌더.
  const visiblePosts = posts.filter((post) => {
    const m = mediaByPost[post.id];
    return !!m && !m.loading && m.thumbs.length > 0;
  });
  // 아직 미디어 로딩이 끝나지 않은 게시글이 남아있는지 (초기 빈 화면 방지용)
  const anyPending = posts.some((post) => {
    const m = mediaByPost[post.id];
    return !m || m.loading;
  });

  if (initialLoading) {
    return (
      <CenterState>
        <Spin size="large" />
      </CenterState>
    );
  }

  return (
    <Container>
      <PageHeader>
        <Eyebrow>ATLAS GALLERY</Eyebrow>
        <Title>{organName}</Title>
        <Count>{visiblePosts.length}{hasMore || anyPending ? "+" : ""} cases</Count>
      </PageHeader>

      {visiblePosts.length === 0 ? (
        anyPending || loadingMore || hasMore ? (
          <CenterState>
            <Spin size="large" />
          </CenterState>
        ) : (
          <CenterState>
            <Empty description="표시할 갤러리가 없습니다." />
          </CenterState>
        )
      ) : (
        <PostGallery>
          {visiblePosts.map((post) => {
            const media = mediaByPost[post.id] ?? emptyMedia;
            return (
              <PostBlock key={post.id}>
                <PostMeta onClick={() => goToPost(post)}>
                  <MetaLeft>
                    <PostTitle>
                      {post.isSecret && <LockOutlined style={{ marginRight: 6, color: "#8c8c8c" }} />}
                      {post.title || "제목 없음"}
                    </PostTitle>
                    <PostWriter>{post.writer || "작성자 미상"}</PostWriter>
                  </MetaLeft>
                  <MetaRight>
                    {media.slideCount > 0 && (
                      <MetaBadge>
                        <FileImageOutlined /> 슬라이드 {media.slideCount}
                      </MetaBadge>
                    )}
                    {media.imageCount > 0 && (
                      <MetaBadge>
                        <PictureOutlined /> 이미지 {media.imageCount}
                      </MetaBadge>
                    )}
                    <ViewLink>자세히 보기 &gt;</ViewLink>
                  </MetaRight>
                </PostMeta>

                <ThumbStrip>
                  {media.thumbs.map((t) => (
                    <ThumbCard key={t.key} onClick={() => goToPost(post)} title={t.label}>
                      <KindTag $kind={t.kind}>
                        {t.kind === "slide" ? "SLIDE" : t.kind === "gross" ? "GROSS" : "IMAGE"}
                      </KindTag>
                      <img
                        src={t.url}
                        alt={t.label || ""}
                        loading="lazy"
                        onError={(e) => {
                          const el = e.currentTarget;
                          el.style.display = "none";
                          (el.parentElement as HTMLElement).classList.add("broken");
                        }}
                      />
                    </ThumbCard>
                  ))}
                </ThumbStrip>
              </PostBlock>
            );
          })}
        </PostGallery>
      )}

      {/* 무한 스크롤 트리거 + 하단 상태 표시 */}
      {posts.length > 0 && (
        <ScrollFooter ref={sentinelRef}>
          {loadingMore && <Spin />}
          {!hasMore && !loadingMore && <EndText>모든 게시글을 불러왔습니다.</EndText>}
        </ScrollFooter>
      )}
    </Container>
  );
};

// 게시글 하나의 슬라이드/이미지/그로스 썸네일을 취합
const fetchPostMedia = async (post: Post): Promise<PostMedia> => {
  if (post.isSecret) {
    return { thumbs: [], slideCount: 0, imageCount: 0, loading: false, locked: true };
  }

  try {
    const [detail, images, grossImages] = await Promise.all([
      getPostData(post.id),
      getImageList(post.id),
      getGrossImageList(post.id),
    ]);

    // 비밀글 등으로 상세 접근이 막힌 경우
    if (isPostDetailBlocked(detail)) {
      return { thumbs: [], slideCount: 0, imageCount: 0, loading: false, locked: true };
    }

    const slides: any[] = detail?.slides ?? post.slides ?? [];
    const photos: any[] = Array.isArray(images) ? images : [];
    const gross: any[] = Array.isArray(grossImages) ? grossImages : [];

    const thumbs: MediaThumb[] = [
      ...slides
        .filter((s) => s?.thumbnail)
        .map((s, i) => ({
          key: `s-${s.slideId ?? i}`,
          url: photoUrl(s.thumbnail),
          kind: "slide" as const,
          label: s.slidename,
        })),
      ...photos
        .filter((p) => p?.pathFileName)
        .map((p, i) => ({
          key: `i-${p.photoId ?? i}`,
          url: photoUrl(p.pathFileName),
          kind: "image" as const,
          label: p.imageName,
        })),
      ...gross
        .filter((g) => g?.pathFileName)
        .map((g, i) => ({
          key: `g-${g.grossPhotoId ?? i}`,
          url: grossUrl(g.pathFileName),
          kind: "gross" as const,
          label: g.caption,
        })),
    ];

    return {
      thumbs,
      slideCount: slides.length,
      imageCount: photos.length + gross.length,
      loading: false,
      locked: false,
    };
  } catch (e) {
    console.error(`post ${post.id} media load error:`, e);
    return { thumbs: [], slideCount: 0, imageCount: 0, loading: false, locked: false };
  }
};

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 2.5rem 4rem 5rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem 3rem;
  }
`;

const CenterState = styled.div`
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-bottom: 2rem;
  margin-bottom: 2.5rem;
  border-bottom: 1px solid #eef1f5;
`;

const Eyebrow = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: ${SubColor};
  margin-bottom: 10px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2.4rem;
  font-weight: 700;
  color: ${MainColor};
  letter-spacing: -0.5px;
`;

const Count = styled.span`
  margin-top: 10px;
  font-size: 0.95rem;
  color: #999;
`;

const PostGallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PostBlock = styled.section`
  background: #fff;
  border: 1px solid #eef1f5;
  border-radius: 16px;
  padding: 1rem 1.75rem 1.25rem;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 8px 26px rgba(0, 0, 0, 0.07);
  }
`;

const PostMeta = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1.1rem;
  margin-bottom: 1rem;
  border-bottom: 1px dashed #e8ebf0;
  cursor: pointer;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const MetaLeft = styled.div`
  min-width: 0;
`;

const PostTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 1.15rem;
  font-weight: 700;
  color: #222;
  line-height: 1.4;
  word-break: keep-all;

  ${PostMeta}:hover & {
    color: ${MainColor};
  }
`;

const PostWriter = styled.div`
  font-size: 0.9rem;
  color: #8a94a3;
`;

const MetaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.82rem;
  color: #667085;
  background: #f4f6f9;
  padding: 4px 10px;
  border-radius: 20px;

  .anticon {
    font-size: 13px;
  }
`;

const ViewLink = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${SubColor};
  white-space: nowrap;
`;

const ThumbStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 14px;
`;

const ThumbCard = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 10px;
  overflow: hidden;
  background: #f0f2f5;
  cursor: pointer;
  border: 1px solid #edf0f4;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s ease;
  }

  &:hover img {
    transform: scale(1.06);
  }

  &.broken::after {
    content: "미리보기 없음";
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #b5bcc7;
    font-size: 0.8rem;
  }
`;

const KindTag = styled.span<{ $kind: MediaKind }>`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
  padding: 3px 7px;
  border-radius: 5px;
  background: ${({ $kind }) =>
    $kind === "slide" ? "rgba(23, 55, 95, 0.88)" : $kind === "gross" ? "rgba(180, 83, 9, 0.88)" : "rgba(48, 104, 164, 0.88)"};
`;

const ScrollFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  padding: 2rem 0 0;
`;

const EndText = styled.span`
  color: #b5bcc7;
  font-size: 0.9rem;
`;

export default Gallery;
